// One source for navigation: the top bar, phone menu, page search and dashboard all read this.
// Visibility rules are unchanged from the old sidebar and dashboard.
import {
  Archive,
  ArrowsLeftRight,
  Barcode,
  Broadcast,
  Camera,
  CardsThree,
  ChartLineUp,
  ClockCounterClockwise,
  CloudArrowUp,
  Coins,
  Cube,
  CurrencyGbp,
  Gift,
  GlobeHemisphereEast,
  ListMagnifyingGlass,
  Package,
  SealCheck,
  ShieldCheck,
  ShoppingCart,
  Storefront,
  Tag,
  Vault,
} from '@phosphor-icons/react';
import { hasPermission, isAdminUser } from '../utils/staffPermissions';
import { canViewInventoryPath, isInventoryOwner, canViewSupplierCosts } from '../utils/inventoryNavigation.mjs';

export const NAV_GROUPS = [
  {
    id: 'slabs',
    label: 'Slabs',
    items: [
      { path: '/browse-slabs', label: 'Browse slabs', description: 'Find slabs, prices and their saved safe locations.', icon: Cube, permission: 'inventory_safes' },
      { path: '/slab-inventory', label: 'Slab inventory', description: 'Receive company slabs and choose where each one goes.', icon: Archive, permission: 'slab_vault' },
      { path: '/slab-inventory/pricing', label: 'Live slab pricing', description: 'Latest matching sold prices for ACE and PSA slabs.', icon: ChartLineUp, permission: 'slab_vault', inventoryOwnerOnly: true },
      { path: '/slab-inventory/whatnot', label: 'Whatnot inventory', description: 'Send slabs to Whatnot as unpublished inventory drafts.', icon: Broadcast, permission: 'slab_vault', inventoryOwnerOnly: true },
      { path: '/slab-inventory/shopify', label: 'For Shopify', description: 'Create drafts, set asking prices and publish slabs.', icon: Storefront, permission: 'inventory_safes' },
      { path: '/slab-inventory/costs', label: 'Add costs', description: 'Scan saved slabs and record what you paid.', icon: CurrencyGbp, permission: 'inventory_safes' },
      { path: '/slab-inventory/review', label: 'Stock review', description: 'Listings, dated comps and paid costs side by side.', icon: ListMagnifyingGlass, permission: 'slab_vault', inventoryOwnerOnly: true },
      { path: '/slab-inventory/price-review', label: 'Website price review', description: 'Review nightly price changes and approve the website price.', icon: SealCheck },
      { path: '/slab-vault', label: 'Slab vault', description: 'Your graded portfolio with values and profit.', icon: Vault, permission: 'slab_vault' },
      { path: '/sealed-vault', label: 'Sealed vault', description: 'Track sealed product, costs and market value.', icon: Package, permission: 'slab_vault' },
      { path: '/slab-surprise', label: 'Slab surprise', description: 'Scan certs into a numbered set and export the sheet.', icon: Gift },
    ],
  },
  {
    id: 'stock',
    label: 'Stock room',
    items: [
      { path: '/stock-inventory', label: 'Browse stock', description: 'Products, quantities and locations from the stock tracker.', icon: Package, permission: 'inventory_safes' },
      { path: '/slab-inventory/storage', label: 'Safe scanner', description: 'Scan box locations, count slabs and put stock away.', icon: Barcode, permission: 'inventory_safes' },
      { path: '/slab-inventory/picks', label: 'Pick & pack', description: 'Shared box runs, removal scans, packing and dispatch.', icon: ShoppingCart, permission: 'inventory_picking', badge: 'picks' },
      { path: '/slab-inventory/transfers', label: 'Container transfers', description: 'Record whole box moves and arrivals.', icon: ArrowsLeftRight, permission: 'inventory_safes' },
      { path: '/slab-inventory/history', label: 'Movement history', description: 'Who handled each slab and where it went.', icon: ClockCounterClockwise, permission: 'inventory_safes' },
    ],
  },
  {
    id: 'streams',
    label: 'Streams',
    items: [
      { path: '/auction-stream-prep', label: 'Auction stream prep', description: 'Scan auction and giveaway slabs into a saved stream pool.', icon: Barcode },
      { path: '/stream-review', label: 'Stream review', description: 'Check Whatnot streams, VAT and save the summary.', icon: Broadcast, permission: 'stream_review' },
      { path: '/stream-performance', label: 'Stream performance', description: 'Compare each streamer against their history and peers.', icon: ChartLineUp, permission: 'stream_performance' },
      { path: '/stream-intelligence', label: 'Stream intelligence', description: 'Card by card profit, slab scans and cost data per stream.', icon: Coins, permission: 'stream_intelligence' },
      { path: '/sales-search', label: 'Sales search', description: 'Search every stream sale by card, streamer or grade.', icon: ListMagnifyingGlass, permission: 'sales_search' },
      { path: '/cardcam-live', label: 'CardCam live', description: 'Live card captures and market values from the stream computers.', icon: Camera },
    ],
  },
  {
    id: 'buying',
    label: 'Buying',
    items: [
      { path: '/buylist-uploader', label: 'Buylist uploader', description: 'Upload a Whatnot export to price a buylist.', icon: CloudArrowUp, permission: 'buylist_uploader' },
      { path: '/japan-cards', label: 'Japan cards', description: 'Japanese stock, purchase orders and grading.', icon: GlobeHemisphereEast, permission: 'japan_cards' },
      { path: '/supplier-orders', label: 'Card costs', description: 'Browse Cardrush and Hareruya cards and purchase prices.', icon: ListMagnifyingGlass, supplierCostsOnly: true },
      { path: '/cardrush', label: 'Cardrush', description: 'Approve buy proposals and hand orders to staff.', icon: Tag, permission: 'cardrush' },
    ],
  },
];

export const ADMIN_ITEM = { path: '/admin', label: 'Staff admin', description: 'Staff accounts and what each person can open.', icon: ShieldCheck, adminOnly: true };
export const HOME_ITEM = { path: '/dashboard', label: 'Home', description: 'Every tool you can open.', icon: CardsThree };

export function canSee(user, item) {
  if (!user) return false;
  if (!canViewInventoryPath(user, item.path)) return false;
  if (item.inventoryOwnerOnly && !isInventoryOwner(user)) return false;
  if (item.supplierCostsOnly && !canViewSupplierCosts(user)) return false;
  if (item.adminOnly) return isAdminUser(user);
  if (!item.permission) return true;
  if (isAdminUser(user)) return true;
  return hasPermission(user, item.permission);
}

export function visibleGroups(user) {
  return NAV_GROUPS
    .map((group) => ({ ...group, items: group.items.filter((item) => canSee(user, item)) }))
    .filter((group) => group.items.length);
}

export function allVisibleItems(user) {
  const items = visibleGroups(user).flatMap((group) => group.items.map((item) => ({ ...item, group: group.label })));
  if (canSee(user, ADMIN_ITEM)) items.push({ ...ADMIN_ITEM, group: 'Admin' });
  return [{ ...HOME_ITEM, group: 'Home' }, ...items];
}

export function isActivePath(pathname, path) {
  if (path === '/slab-inventory') return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

// Pages with no menu entry of their own light up the tool they belong to.
const RELATED_PATHS = { '/slab-inventory/orders': '/slab-inventory/shopify' };

// The deepest matching item wins, so /slab-inventory/storage/labels highlights Safe scanner.
export function activeItem(pathname, items) {
  const match = (path) => items
    .filter((item) => isActivePath(path, item.path))
    .sort((a, b) => b.path.length - a.path.length)[0] || null;
  return (RELATED_PATHS[pathname] && match(RELATED_PATHS[pathname])) || match(pathname);
}
